import {
	alpha,
	Box,
	ButtonBase,
	Divider,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { Plus } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import {
	EXAMPLE_GOAL_CATEGORIES,
	type ExampleGoalCategory,
} from "web_myplann/Goals/DummyData/goalsDummyData";
import { GOAL_CATEGORY_CONFIG } from "web_myplann/Goals/Types/GoalTypes";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getRoute } from "web_myplann/Infrastructure/Utils/UrlUtils";

const EXAMPLE_RESOURCES = {
	Retirement: Resources.Goals.EmptyState.Examples.Retirement,
	Housing: Resources.Goals.EmptyState.Examples.Housing,
	Vacation: Resources.Goals.EmptyState.Examples.Vacation,
} as const satisfies Record<
	ExampleGoalCategory,
	{ name: unknown; reason: unknown }
>;

export const ExampleGoalList: FC = () => {
	const { palette } = useTheme();
	const { t } = useResource();
	const navigate = useNavigate();

	const handleSelect = (category: ExampleGoalCategory) => {
		navigate(`${getRoute(Routing.Goals.GoalFormCreate)}?category=${category}`);
	};

	return (
		<Stack bgcolor={palette.background.paper}>
			{EXAMPLE_GOAL_CATEGORIES.map((category, i) => {
				const config = GOAL_CATEGORY_CONFIG[category];
				const Icon = config.icon;
				const resource = EXAMPLE_RESOURCES[category];

				return (
					<Box key={category}>
						<ButtonBase
							onClick={() => handleSelect(category)}
							sx={{ width: "100%", textAlign: "left" }}
						>
							<Stack
								direction="row"
								alignItems="center"
								gap={1.4}
								px={1.6}
								py={1.4}
								width="100%"
							>
								<Box
									sx={{
										width: 40,
										height: 40,
										borderRadius: "12px",
										bgcolor: palette.grey[100],
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										flexShrink: 0,
										border: `1px dashed ${palette.grey[300]}`,
									}}
								>
									<Icon size={20} color={config.color} />
								</Box>
								<Stack flex={1} minWidth={0}>
									<Typography
										fontSize={15}
										fontWeight={500}
										color={palette.grey[500]}
									>
										{t(resource.name)}
									</Typography>
									<Typography fontSize={12} color={palette.grey[400]} mt={0.2}>
										{t(resource.reason)}
									</Typography>
								</Stack>
								<Stack
									direction="row"
									alignItems="center"
									gap={0.4}
									flexShrink={0}
								>
									<Typography
										fontSize={12}
										fontWeight={500}
										color={palette.primary.main}
									>
										{t(Resources.Goals.EmptyState.addLabel)}
									</Typography>
									<Plus size={14} color={palette.primary.main} />
								</Stack>
							</Stack>
						</ButtonBase>
						{i < EXAMPLE_GOAL_CATEGORIES.length - 1 && (
							<Divider
								sx={{
									borderColor: alpha(palette.grey[100], 0.6),
								}}
							/>
						)}
					</Box>
				);
			})}
		</Stack>
	);
};
